/**
 * PRD.219 + PRD.229 — Patient Billing Across Visits.
 *
 * One chronological "billing events" table that mixes invoices,
 * payments, and adjustments per PRD.229 §5.6 / §7.2.1. A single top-
 * right "+ New Record" action opens the same TreatmentInvoiceDrawer
 * the Treatment Record Peso quick-action uses, so the Billing tab
 * doesn't introduce a second separate billing-entry UI.
 *
 * Row click on an Invoice or invoice-linked Payment/Adjustment opens
 * the invoice detail drawer (same InvoicesTab single-pane the rest of
 * the app uses), so users can drill in without losing context.
 */

import { useMemo, useState } from 'react';
import { usePowerSyncDb, usePowerSyncQuery } from '../../adapters/powersync';
import {
  mapPatientWorkspaceSettingsToUI,
  qBillingAdjustmentsByPatient,
  qPatientInvoicesWithPaid,
  qPatientWorkspaceSettings,
  qPaymentsByPatient,
  parsePaymentAllocations,
  type BillingAdjustmentRow,
  type InvoicePaymentRow,
  type PatientInvoiceRow,
  type PatientWorkspaceSettingsRow,
} from '@helios/data-access';
import { NavIcon, RightDrawer } from '../../shell/shared';
import { phpFromCents } from '@helios/domain';
import { useFormatMoney } from '../../hooks/useFormatMoney';
import { InvoicesTab } from '../patient/consultation/InvoicesTab';
import { RecordPaymentDialog } from './RecordPaymentDialog';
import { PatientItemPaymentStatus } from './PatientItemPaymentStatus';
import { usePatientBilling } from './usePatientBilling';
// PRD.229 — Billing tab "+ New Record" reuses the same Add Invoice
// surface the Treatment Record Peso quick-action opens, so the Billing
// tab doesn't introduce a second separate billing-entry UI.
import { TreatmentInvoiceDrawer } from '../patient/encounters/TreatmentInvoiceDrawer';
import { ensureEncounterForDate } from '../../services/encounter-service';
import { todayLocalISO } from '../patient/encounters/DateChipPopover';

// ─── Unified billing event model ────────────────────────────────────────────
//
// All three event kinds collapse into one polymorphic discriminator
// because the table renders columns shared across all kinds (Date /
// Type / Description / Amount / Actions). The original rows ride along
// in `raw` so row click handlers can reach back to invoice id / etc.

type BillingEvent =
  | {
      kind: 'invoice';
      id: string;
      /** ISO date or timestamp used for sorting. */
      sortKey: string;
      raw: PatientInvoiceRow;
    }
  | {
      kind: 'payment';
      id: string;
      sortKey: string;
      raw: InvoicePaymentRow;
    }
  | {
      kind: 'adjustment';
      id: string;
      sortKey: string;
      raw: BillingAdjustmentRow;
    };

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-PH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

const METHOD_LABEL: Record<string, string> = {
  cash: 'Cash',
  card: 'Card',
  bank_transfer: 'Bank Transfer',
  gcash: 'GCash',
  other: 'Other',
};

// ─── Props ───

interface Props {
  patientId: string;
  patientName: string;
  orgId: string;
}

// ─── View ───

export function BillingAcrossVisitsView({ patientId, patientName, orgId }: Props) {
  const { data: invoiceRows = [] } = usePowerSyncQuery<PatientInvoiceRow>(
    qPatientInvoicesWithPaid(patientId),
  );
  const { data: paymentRows = [] } = usePowerSyncQuery<InvoicePaymentRow>(
    qPaymentsByPatient(patientId),
  );
  const { data: adjustmentRows = [] } = usePowerSyncQuery<BillingAdjustmentRow>(
    qBillingAdjustmentsByPatient(patientId),
  );

  // PRD.227 — Shared Dinero-backed aggregate that already includes
  // debit + credit adjustments + invoice-linked + patient-level payments.
  const billing = usePatientBilling(patientId);
  const fmt = useFormatMoney();

  const [drawerInvoice, setDrawerInvoice] = useState<PatientInvoiceRow | null>(null);
  const [payInvoice, setPayInvoice] = useState<PatientInvoiceRow | null>(null);
  // When set, the Record Payment dialog opens in edit mode so the user can
  // allocate (or re-allocate) an existing payment to specific invoice items.
  const [editPayment, setEditPayment] = useState<InvoicePaymentRow | null>(null);
  // PRD.229 — "+ New Record" opens the same TreatmentInvoiceDrawer the
  // Peso quick-action uses. We resolve today's encounter up front so the
  // drawer renders the editor immediately (its `ready` gate needs both
  // encounterId + date set).
  const { db } = usePowerSyncDb();
  const [invoiceDrawer, setInvoiceDrawer] = useState<{
    encounterId: string;
    date: string;
  } | null>(null);
  const [resolvingNewRecord, setResolvingNewRecord] = useState(false);

  // Drawer width preset is configured per-clinic in Patient Workspace
  // settings; the Peso button reads the same value via
  // TreatmentRecordQuickToolbarHost, so reuse it here for consistency.
  const { data: workspaceRows = [] } = usePowerSyncQuery<PatientWorkspaceSettingsRow>(
    qPatientWorkspaceSettings(),
  );
  const invoiceDrawerWidth =
    workspaceRows.length > 0
      ? mapPatientWorkspaceSettingsToUI(workspaceRows[0]).treatmentRecordDrawerWidth
      : 'standard';

  async function handleOpenNewRecord() {
    if (!db || !orgId || !patientId || resolvingNewRecord) return;
    setResolvingNewRecord(true);
    try {
      const date = todayLocalISO();
      const { encounterId } = await ensureEncounterForDate({
        db,
        orgId,
        patientId,
        date,
      });
      setInvoiceDrawer({ encounterId, date });
    } catch (err) {
      console.error('[BillingAcrossVisitsView] resolve encounter for New Record:', err);
    } finally {
      setResolvingNewRecord(false);
    }
  }

  const invoiceById = useMemo(() => {
    const map = new Map<string, PatientInvoiceRow>();
    for (const inv of invoiceRows) map.set(inv.id, inv);
    return map;
  }, [invoiceRows]);

  // Merge + sort: newest first. Tie-break on kind so payments fall under
  // their parent invoice when timestamps collide.
  const events: BillingEvent[] = useMemo(() => {
    const out: BillingEvent[] = [];
    for (const inv of invoiceRows) {
      out.push({
        kind: 'invoice',
        id: `inv-${inv.id}`,
        sortKey: inv.date_iso ?? inv.created_at ?? '',
        raw: inv,
      });
    }
    for (const p of paymentRows) {
      out.push({
        kind: 'payment',
        id: `pay-${p.id}`,
        sortKey: p.paid_at ?? p.created_at ?? '',
        raw: p,
      });
    }
    for (const a of adjustmentRows) {
      out.push({
        kind: 'adjustment',
        id: `adj-${a.id}`,
        sortKey: a.adjusted_at ?? a.created_at ?? '',
        raw: a,
      });
    }
    out.sort((x, y) => {
      const cmp = y.sortKey.localeCompare(x.sortKey);
      if (cmp !== 0) return cmp;
      // Invoices first on tied dates so the parent appears above its payments/adjustments.
      const order = { invoice: 0, payment: 1, adjustment: 2 } as const;
      return order[x.kind] - order[y.kind];
    });
    return out;
  }, [invoiceRows, paymentRows, adjustmentRows]);

  const totals = useMemo(
    () => ({
      total: fmt(billing.charged),
      paid: fmt(billing.paid),
      balance: fmt(billing.balance),
      debit: fmt(billing.debit),
      credit: fmt(billing.credit),
      invoiceCount: invoiceRows.length,
    }),
    [billing, invoiceRows.length, fmt],
  );

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* PRD.229 §5.1/§7.1 — Top-right action area. */}
      <div
        style={{
          padding: 'var(--sp-3) var(--sp-5) var(--sp-2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 'var(--sp-2)',
          flexShrink: 0,
          flexWrap: 'wrap',
        }}
      >
        {/* PRD.229 §5 / §7 — Single "+ New Record" action reuses the same
            Add Invoice surface the Treatment Record Peso quick-action
            opens (TreatmentInvoiceDrawer). No second separate billing-
            entry UI for the Billing tab. */}
        <ActionButton tone="primary" onClick={() => void handleOpenNewRecord()}>
          {resolvingNewRecord ? 'Opening…' : '+ New Record'}
        </ActionButton>
      </div>

      {/* Sum-of-events strip — Dinero-backed, includes adjustments + patient-level payments. */}
      <div
        style={{
          padding: '0 var(--sp-5) var(--sp-3)',
          display: 'flex',
          alignItems: 'baseline',
          gap: 'var(--sp-4)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--text-muted)',
          flexShrink: 0,
          flexWrap: 'wrap',
        }}
      >
        <span>
          {totals.invoiceCount} invoice{totals.invoiceCount === 1 ? '' : 's'}
        </span>
        <span>·</span>
        <span>
          Total <strong style={{ color: 'var(--text)' }}>{totals.total}</strong>
        </span>
        <span>
          Paid <strong style={{ color: 'var(--color-success-text)' }}>{totals.paid}</strong>
        </span>
        {billing.debitCents > 0 && (
          <span>
            Debits <strong style={{ color: 'var(--color-warning-text)' }}>{totals.debit}</strong>
          </span>
        )}
        {billing.creditCents > 0 && (
          <span>
            Credits <strong style={{ color: 'var(--color-success-text)' }}>{totals.credit}</strong>
          </span>
        )}
        <span>
          Remaining <strong style={{ color: 'var(--color-warning-text)' }}>{totals.balance}</strong>
        </span>
      </div>

      {/* Chronological billing events table. */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
        }}
      >
        <div style={{ padding: '0 var(--sp-5) var(--sp-5)' }}>
          {events.length === 0 ? (
            <EmptyState />
          ) : (
            <div
              style={{
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-md)',
                overflow: 'hidden',
                background: 'var(--bg-surface)',
              }}
            >
              <table style={tableStyle}>
                <thead>
                  <tr style={{ background: 'var(--bg-muted, #f9fafb)' }}>
                    <Th>Date</Th>
                    <Th>Type</Th>
                    <Th>Description</Th>
                    <Th align="right">Amount</Th>
                    <Th align="right">Actions</Th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((evt) =>
                    evt.kind === 'invoice' ? (
                      <InvoiceEventRow
                        key={evt.id}
                        row={evt.raw}
                        onOpen={() => setDrawerInvoice(evt.raw)}
                        onPay={() => setPayInvoice(evt.raw)}
                      />
                    ) : evt.kind === 'payment' ? (
                      <PaymentEventRow
                        key={evt.id}
                        row={evt.raw}
                        linkedInvoice={
                          evt.raw.invoice_id ? (invoiceById.get(evt.raw.invoice_id) ?? null) : null
                        }
                        onOpenInvoice={() => {
                          const inv = evt.raw.invoice_id
                            ? invoiceById.get(evt.raw.invoice_id)
                            : null;
                          if (inv) setDrawerInvoice(inv);
                        }}
                        onAllocate={() => setEditPayment(evt.raw)}
                      />
                    ) : (
                      <AdjustmentEventRow
                        key={evt.id}
                        row={evt.raw}
                        linkedInvoice={
                          evt.raw.invoice_id ? (invoiceById.get(evt.raw.invoice_id) ?? null) : null
                        }
                        onOpenInvoice={() => {
                          const inv = evt.raw.invoice_id
                            ? invoiceById.get(evt.raw.invoice_id)
                            : null;
                          if (inv) setDrawerInvoice(inv);
                        }}
                      />
                    ),
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* PRD.247 — Billing Item Payment Status: the primary item-level
              settlement summary across all of the patient's invoices. */}
          <div style={{ marginTop: 'var(--sp-5)' }}>
            <PatientItemPaymentStatus patientId={patientId} />
          </div>
        </div>
      </div>

      {/* Invoice detail drawer — same pattern as GlobalInvoicesPage. */}
      <RightDrawer
        open={!!drawerInvoice}
        onClose={() => setDrawerInvoice(null)}
        title={
          drawerInvoice
            ? `Invoice ${drawerInvoice.invoice_no ?? drawerInvoice.id.slice(0, 8)}`
            : 'Invoice'
        }
        icon="file-text"
        expandable
        initialExpanded
        expandedWidth="min(1200px, 90%)"
        contentPadding="var(--sp-4)"
      >
        {drawerInvoice && (
          <InvoicesTab
            key={drawerInvoice.id}
            patientId={patientId}
            patientName={patientName}
            initialInvoiceId={drawerInvoice.id}
            hideHeader
            singlePane
          />
        )}
      </RightDrawer>

      {/* Invoice-locked Record Payment dialog — fired by the row "Pay" action. */}
      <RecordPaymentDialog
        open={!!payInvoice}
        onOpenChange={(open) => {
          if (!open) setPayInvoice(null);
        }}
        invoice={
          payInvoice
            ? {
                id: payInvoice.id,
                invoice_no: payInvoice.invoice_no,
                grand_total: payInvoice.grand_cents / 100,
                status: payInvoice.status,
              }
            : null
        }
        patientId={patientId}
        orgId={orgId}
      />

      {/* PRD.247 — Edit/allocate an existing payment from the Billing page.
          Reuses the Record Payment dialog in edit mode, which already has the
          allocate-to-items section. Only invoice-linked payments are editable
          here (allocation needs an invoice's items). */}
      {(() => {
        const inv = editPayment?.invoice_id ? invoiceById.get(editPayment.invoice_id) : null;
        return (
          <RecordPaymentDialog
            open={!!editPayment && !!inv}
            onOpenChange={(open) => {
              if (!open) setEditPayment(null);
            }}
            invoice={
              inv
                ? {
                    id: inv.id,
                    invoice_no: inv.invoice_no,
                    grand_total: inv.grand_cents / 100,
                    status: inv.status,
                  }
                : null
            }
            patientId={patientId}
            orgId={orgId}
            editPayment={editPayment}
          />
        );
      })()}

      {/* PRD.229 — Same drawer the Peso quick-action opens. */}
      <TreatmentInvoiceDrawer
        open={invoiceDrawer != null}
        onOpenChange={(o) => {
          if (!o) setInvoiceDrawer(null);
        }}
        patientId={patientId}
        initialEncounterId={invoiceDrawer?.encounterId ?? null}
        initialDate={invoiceDrawer?.date ?? null}
        drawerWidth={invoiceDrawerWidth}
      />
    </div>
  );
}

// ─── Action button ───

function ActionButton({
  tone,
  onClick,
  children,
}: {
  tone: 'primary' | 'success' | 'warning';
  onClick: () => void;
  children: React.ReactNode;
}) {
  const palette = {
    primary: { border: 'var(--color-primary)', color: 'var(--color-primary)' },
    success: { border: '#16a34a', color: '#16a34a' },
    warning: { border: 'var(--color-warning-text)', color: 'var(--color-warning-text)' },
  }[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        height: 32,
        padding: '0 var(--sp-3)',
        borderRadius: 'var(--radius-md)',
        border: `1px solid ${palette.border}`,
        background: 'var(--bg-surface)',
        color: palette.color,
        fontSize: 'var(--font-size-sm)',
        fontWeight: 600,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
}

// ─── Empty state ───

function EmptyState() {
  return (
    <div
      style={{
        marginTop: 'var(--sp-6)',
        padding: 'var(--sp-6)',
        borderRadius: 'var(--radius-md)',
        border: '1px dashed var(--border)',
        background: 'var(--bg-surface)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 'var(--sp-2)',
        color: 'var(--text-muted)',
        fontSize: 'var(--font-size-sm)',
      }}
    >
      <span style={{ color: 'var(--text-muted)', display: 'flex' }}>
        <NavIcon name="file-text" />
      </span>
      <div style={{ fontWeight: 'var(--font-weight-semi)', color: 'var(--text)' }}>
        No billing activity yet
      </div>
      <div>Use the buttons above to add an invoice, record a payment, or post an adjustment.</div>
    </div>
  );
}

// ─── Event row components ───

function InvoiceEventRow({
  row,
  onOpen,
  onPay,
}: {
  row: PatientInvoiceRow;
  onOpen: () => void;
  onPay: () => void;
}) {
  const fmt = useFormatMoney();
  const status = (row.status ?? 'draft') as 'draft' | 'issued' | 'paid';
  const isPaid = row.balance_cents <= 0 && row.grand_cents > 0;
  const pillTone = (() => {
    if (status === 'paid' || isPaid)
      return { bg: 'var(--color-success-bg)', color: 'var(--color-success-text)', label: 'Paid' };
    if (status === 'issued' && row.paid_cents > 0)
      return {
        bg: 'var(--color-warning-bg)',
        color: 'var(--color-warning-text)',
        label: 'Partial',
      };
    if (status === 'issued')
      return {
        bg: 'var(--color-info-bg, #eff6ff)',
        color: 'var(--color-info, #2563eb)',
        label: 'Issued',
      };
    return { bg: 'var(--bg-surface-2)', color: 'var(--text-muted)', label: 'Draft' };
  })();

  return (
    <tr onClick={onOpen} style={{ borderTop: '1px solid var(--border)', cursor: 'pointer' }}>
      <Td>{fmtDate(row.date_iso ?? row.created_at)}</Td>
      <Td>
        <KindPill kind="invoice" />
      </Td>
      <Td>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>
            {row.invoice_no ?? row.id.slice(0, 8)}
          </span>
          <span
            style={{
              display: 'inline-block',
              padding: '1px 8px',
              borderRadius: 'var(--radius-full)',
              background: pillTone.bg,
              color: pillTone.color,
              fontSize: 'var(--font-size-xs)',
              fontWeight: 500,
            }}
          >
            {pillTone.label}
          </span>
          <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)' }}>
            ({fmt(phpFromCents(row.paid_cents))} paid /{' '}
            {fmt(phpFromCents(Math.max(0, row.balance_cents)))} remaining)
          </span>
        </div>
      </Td>
      <Td align="right" mono>
        {fmt(phpFromCents(row.grand_cents))}
      </Td>
      <Td align="right">
        <div
          style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}
          onClick={(e) => e.stopPropagation()}
        >
          {!isPaid && row.balance_cents > 0 && (
            <button
              type="button"
              onClick={onPay}
              title="Record a payment for this invoice"
              style={payBtnStyle}
            >
              Pay
            </button>
          )}
          <button type="button" onClick={onOpen} style={openBtnStyle}>
            Open
          </button>
        </div>
      </Td>
    </tr>
  );
}

function PaymentEventRow({
  row,
  linkedInvoice,
  onOpenInvoice,
  onAllocate,
}: {
  row: InvoicePaymentRow;
  linkedInvoice: PatientInvoiceRow | null;
  onOpenInvoice: () => void;
  onAllocate: () => void;
}) {
  const fmt = useFormatMoney();
  const method = METHOD_LABEL[row.method ?? ''] ?? row.method ?? 'Payment';
  // Only offer "Allocate" when the payment has an unallocated remainder — a
  // fully item-allocated payment has nothing left to assign.
  const allocatedCents = parsePaymentAllocations(row.allocations).reduce(
    (s, a) => s + a.amount_cents,
    0,
  );
  const canAllocate = !!linkedInvoice && allocatedCents < (row.amount_cents ?? 0);
  return (
    <tr style={{ borderTop: '1px solid var(--border)' }}>
      <Td>{fmtDate(row.paid_at ?? row.created_at)}</Td>
      <Td>
        <KindPill kind="payment" />
      </Td>
      <Td>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>{method}</span>
          {linkedInvoice ? (
            <button
              type="button"
              onClick={onOpenInvoice}
              style={linkBtnStyle}
              title="Open linked invoice"
            >
              · {linkedInvoice.invoice_no ?? linkedInvoice.id.slice(0, 8)}
            </button>
          ) : (
            <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)' }}>
              · Patient-level
            </span>
          )}
          {row.notes && (
            <span
              style={{
                color: 'var(--text-muted)',
                fontSize: 'var(--font-size-xs)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {row.notes}
            </span>
          )}
        </div>
      </Td>
      <Td align="right" mono color="var(--color-success-text)">
        −{fmt(phpFromCents(row.amount_cents ?? 0))}
      </Td>
      <Td align="right">
        {canAllocate && (
          <button type="button" onClick={onAllocate} style={linkBtnStyle} title="Allocate to items">
            Allocate
          </button>
        )}
      </Td>
    </tr>
  );
}

function AdjustmentEventRow({
  row,
  linkedInvoice,
  onOpenInvoice,
}: {
  row: BillingAdjustmentRow;
  linkedInvoice: PatientInvoiceRow | null;
  onOpenInvoice: () => void;
}) {
  const fmt = useFormatMoney();
  const isDebit = row.adjustment_type === 'debit';
  return (
    <tr style={{ borderTop: '1px solid var(--border)' }}>
      <Td>{fmtDate(row.adjusted_at ?? row.created_at)}</Td>
      <Td>
        <KindPill kind={isDebit ? 'debit' : 'credit'} />
      </Td>
      <Td>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span>{row.reason ?? '—'}</span>
          {linkedInvoice && (
            <button
              type="button"
              onClick={onOpenInvoice}
              style={linkBtnStyle}
              title="Open linked invoice"
            >
              · {linkedInvoice.invoice_no ?? linkedInvoice.id.slice(0, 8)}
            </button>
          )}
          {row.note && (
            <span
              style={{
                color: 'var(--text-muted)',
                fontSize: 'var(--font-size-xs)',
              }}
            >
              {row.note}
            </span>
          )}
        </div>
      </Td>
      <Td
        align="right"
        mono
        color={isDebit ? 'var(--color-warning-text)' : 'var(--color-success-text)'}
      >
        {isDebit ? '+' : '−'}
        {fmt(phpFromCents(row.amount_cents ?? 0))}
      </Td>
      <Td />
    </tr>
  );
}

// ─── Kind pill ───

function KindPill({ kind }: { kind: 'invoice' | 'payment' | 'debit' | 'credit' }) {
  const styles: Record<typeof kind, { bg: string; color: string; label: string }> = {
    invoice: {
      bg: 'var(--color-info-bg, #eff6ff)',
      color: 'var(--color-info, #2563eb)',
      label: 'Invoice',
    },
    payment: {
      bg: 'var(--color-success-bg)',
      color: 'var(--color-success-text)',
      label: 'Payment',
    },
    debit: {
      bg: 'var(--color-warning-bg)',
      color: 'var(--color-warning-text)',
      label: 'Debit',
    },
    credit: {
      bg: 'var(--color-success-bg)',
      color: 'var(--color-success-text)',
      label: 'Credit',
    },
  };
  const s = styles[kind];
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '1px 8px',
        borderRadius: 'var(--radius-full)',
        background: s.bg,
        color: s.color,
        fontSize: 'var(--font-size-xs)',
        fontWeight: 500,
      }}
    >
      {s.label}
    </span>
  );
}

// ─── Cell helpers ───

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 'var(--font-size-sm)',
};

function Th({ children, align }: { children: React.ReactNode; align?: 'right' }) {
  return (
    <th
      style={{
        textAlign: align ?? 'left',
        padding: 'var(--sp-2) var(--sp-3)',
        fontWeight: 600,
        color: 'var(--text-muted)',
        fontSize: 'var(--font-size-xs)',
        textTransform: 'uppercase',
        letterSpacing: 0.4,
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align,
  mono,
  color,
}: {
  children?: React.ReactNode;
  align?: 'right';
  mono?: boolean;
  color?: string;
}) {
  return (
    <td
      style={{
        textAlign: align ?? 'left',
        padding: 'var(--sp-2) var(--sp-3)',
        color: color ?? 'var(--text)',
        fontVariantNumeric: mono ? 'tabular-nums' : undefined,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </td>
  );
}

const payBtnStyle: React.CSSProperties = {
  height: 28,
  padding: '0 var(--sp-3)',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid #16a34a',
  background: 'var(--bg-surface)',
  color: '#16a34a',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semi)',
  cursor: 'pointer',
};

const openBtnStyle: React.CSSProperties = {
  height: 28,
  padding: '0 var(--sp-3)',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--color-primary)',
  background: 'var(--bg-surface)',
  color: 'var(--color-primary)',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semi)',
  cursor: 'pointer',
};

const linkBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'var(--color-primary, #4f46e5)',
  fontSize: 'var(--font-size-xs)',
  cursor: 'pointer',
  padding: 0,
  textDecoration: 'underline',
  textDecorationStyle: 'dotted',
  textUnderlineOffset: 2,
};
